import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { SmsTemplate, SmsTemplateState } from '@/types/sms';

const initialState: SmsTemplateState = {
  templates: [],
  templatesLoading: false,
  templatesError: null,
  
  selectedTemplate: null,
  selectedTemplateLoading: false,
  selectedTemplateError: null,
  
  createLoading: false,
  createError: null,
  
  updateLoading: false,
  updateError: null,
  
  deleteLoading: false,
  deleteError: null,
  
  filters: {},
};

const smsTemplateSlice = createSlice({
  name: 'smsTemplate',
  initialState,
  reducers: {
    // Fetch all templates
    fetchTemplatesRequested: (state, action: PayloadAction<{
      lob_type?: 'MOTOR' | 'HEALTH' | 'ALL';
      sms_type?: 'notice_only' | 'notice_with_link';
      is_active?: boolean;
      name?: string;
    } | undefined>) => {
      state.templatesLoading = true;
      state.templatesError = null;
      if (action.payload) {
        state.filters = action.payload;
      }
    },
    fetchTemplatesSucceeded: (state, action: PayloadAction<SmsTemplate[]>) => {
      state.templatesLoading = false;
      state.templates = action.payload;
      state.templatesError = null;
    },
    fetchTemplatesFailed: (state, action: PayloadAction<string>) => {
      state.templatesLoading = false;
      state.templatesError = action.payload;
    },
    
    // Fetch single template
    fetchTemplateRequested: (state, action: PayloadAction<number>) => {
      state.selectedTemplateLoading = true;
      state.selectedTemplateError = null;
    },
    fetchTemplateSucceeded: (state, action: PayloadAction<SmsTemplate>) => {
      state.selectedTemplateLoading = false;
      state.selectedTemplate = action.payload;
      state.selectedTemplateError = null;
    },
    fetchTemplateFailed: (state, action: PayloadAction<string>) => {
      state.selectedTemplateLoading = false;
      state.selectedTemplateError = action.payload;
    },
    
    // Create template
    createTemplateRequested: (state, action: PayloadAction<any>) => {
      state.createLoading = true;
      state.createError = null;
    },
    createTemplateSucceeded: (state, action: PayloadAction<SmsTemplate>) => {
      state.createLoading = false;
      state.templates.unshift(action.payload);
      state.createError = null;
    },
    createTemplateFailed: (state, action: PayloadAction<string>) => {
      state.createLoading = false;
      state.createError = action.payload;
    },
    
    // Update template
    updateTemplateRequested: (state, action: PayloadAction<{ id: number; data: any }>) => {
      state.updateLoading = true;
      state.updateError = null;
    },
    updateTemplateSucceeded: (state, action: PayloadAction<SmsTemplate>) => {
      state.updateLoading = false;
      const index = state.templates.findIndex(t => t.id === action.payload.id);
      if (index !== -1) {
        state.templates[index] = action.payload;
      }
      if (state.selectedTemplate?.id === action.payload.id) {
        state.selectedTemplate = action.payload;
      }
      state.updateError = null;
    },
    updateTemplateFailed: (state, action: PayloadAction<string>) => {
      state.updateLoading = false;
      state.updateError = action.payload;
    },
    
    // Delete template
    deleteTemplateRequested: (state, action: PayloadAction<number>) => {
      state.deleteLoading = true;
      state.deleteError = null;
    },
    deleteTemplateSucceeded: (state, action: PayloadAction<number>) => {
      state.deleteLoading = false;
      // Mark template as inactive instead of removing
      const template = state.templates.find(t => t.id === action.payload);
      if (template) {
        template.is_active = false;
      }
      state.deleteError = null;
    },
    deleteTemplateFailed: (state, action: PayloadAction<string>) => {
      state.deleteLoading = false;
      state.deleteError = action.payload;
    },
    
    // Clear selected template
    clearSelectedTemplate: (state) => {
      state.selectedTemplate = null;
      state.selectedTemplateError = null;
    },
    
    // Clear errors
    clearTemplateErrors: (state) => {
      state.templatesError = null;
      state.selectedTemplateError = null;
      state.createError = null;
      state.updateError = null;
      state.deleteError = null;
    },
    
    // Set filters
    setTemplateFilters: (state, action: PayloadAction<{
      lob_type?: 'MOTOR' | 'HEALTH' | 'ALL';
      sms_type?: 'notice_only' | 'notice_with_link';
      is_active?: boolean;
    }>) => {
      state.filters = action.payload;
    },
  },
});

export const {
  fetchTemplatesRequested,
  fetchTemplatesSucceeded,
  fetchTemplatesFailed,
  fetchTemplateRequested,
  fetchTemplateSucceeded,
  fetchTemplateFailed,
  createTemplateRequested,
  createTemplateSucceeded,
  createTemplateFailed,
  updateTemplateRequested,
  updateTemplateSucceeded,
  updateTemplateFailed,
  deleteTemplateRequested,
  deleteTemplateSucceeded,
  deleteTemplateFailed,
  clearSelectedTemplate,
  clearTemplateErrors,
  setTemplateFilters,
} = smsTemplateSlice.actions;

export default smsTemplateSlice.reducer;


